/**
 * search_unify1 s-ui — 「{지역} 인기 단지 · 인기 검색어」 표시 층(클라이언트 · 순수 함수 · 테스트 대상).
 *
 * 데이터 계약 = s-pop(search-b) GET /api/search/popular?sido=&sigungu=[&limit][&exclude]
 *   → { sites:[{slug,name,rank,prevRank?,sigungu?,units?,stageLabel?,stageTone?('sub'|'upcoming'|'redev'|'default')}],
 *       keywords: string[], label(「최근 7일 관심 기준 · 매일 갱신」 | 대체 「일정 임박 단지 · 매일 갱신」), scope(「부산」·「부산 해운대구」), fallback }
 *   (서버 층 = src/lib/search/popular.ts loadRegionPopular — s-pop 소유. 이 파일은 그 JSON 을 화면 값으로만 바꾼다.)
 * 시군구 탭 = GET /api/search/popular-tabs?sido= (rpc get_popular_sigungu_tabs · thin=true 숨김).
 * 노출 플래그 ui.popular_region(app_config · 기본 off) 은 서버가 읽어 NavData 로 내려준다(lib/search/popular-flag).
 * ⛔ 조회수·검색수 수치 비노출(순위만). 「실시간」 금지. 목업은 테스트 픽스처에만 — 실패·빈 값은 블록째 생략.
 */
import { sidoDisplayLabel } from '@/lib/ds2/sido-label';
import { displaySiteName, placeOf, sidoKey, subline } from './subline';
import { ownPhotoSrc, unitsText, shortSigungu, type ThumbData, type ThumbTone } from '@/lib/thumb/resolve';

export const POPULAR_NOTE = '최근 7일 관심 기준 · 매일 갱신';
export const POPULAR_FALLBACK_NOTE = '일정 임박 단지 · 매일 갱신';

/** 표시 색 4종 — 청약 D-n(sub) · 분양예정(upcoming) · 재개발·재건축(redev) · 그 외(default). */
export type StageTone = 'sub' | 'upcoming' | 'redev' | 'default';

export interface PopularSite {
  rank: number;
  prevRank: number | null;
  slug: string;
  name: string;
  sigungu: string | null;
  units: number | null;
  stageLabel: string | null;
  stageTone: StageTone;
  /** thumb-lists — 행 썸네일(서버가 실어 줄 때만 이미지 · 없으면 단계 타일). */
  thumb?: ThumbData;
}

const TONE_OF: Record<StageTone, ThumbTone> = { sub: 'sale', upcoming: 'plan', redev: 'redev', default: 'done' };

/** thumb-lists — 서버 thumb(검증) 또는 단계 타일. 이미지 주소는 자체 경로만 다시 확인한다(외부·글자 카드 0). */
export function popularThumbOf(raw: unknown, s: Pick<PopularSite, 'stageTone' | 'sigungu' | 'units'>): ThumbData {
  const main = unitsText(s.units);
  const tile = { tone: TONE_OF[s.stageTone] ?? 'done', label: shortSigungu(s.sigungu), main, numeric: !!main };
  const t = (raw ?? null) as { img?: Record<string, unknown> | null } | null;
  const im = t?.img ?? null;
  const src = im && typeof im.src === 'string' ? ownPhotoSrc(im.src) : null;
  const alt = im && typeof im.alt === 'string' ? im.alt.trim() : '';
  const ai = !!im?.ai;
  const caption = im && typeof im.caption === 'string' ? im.caption.trim() : '';
  const img = src && alt && (!ai || caption)
    ? { src, alt, ai, caption: ai ? caption : null, width: typeof im!.width === 'number' ? im!.width : null, height: typeof im!.height === 'number' ? im!.height : null }
    : null;
  return { img, tile };
}

/** keyword = 검색어 값(원문 · 링크·검색에 그대로) · label = 칩에 보일 글자(붙여 쓴 KW 원문이면 공백 무시 일치하는 단지 표시명) · slug = 그 단지(같은 단지 칩 1개). */
export interface PopularKeyword { rank: number; keyword: string; label: string; slug?: string | null }

/** #4g.4 — 서버 keywordItems({keyword, label?, slug?})를 칩에 입힌다(label ?? keyword). 없으면 그대로. */
export function withServerLabels(kws: PopularKeyword[], items: unknown): PopularKeyword[] {
  if (!Array.isArray(items)) return kws;
  const by = new Map<string, { label: string | null; slug: string | null }>();
  for (const it of items as Record<string, unknown>[]) {
    const k = typeof it?.keyword === 'string' ? it.keyword.trim() : '';
    if (k) by.set(k, { label: typeof it.label === 'string' && it.label.trim() ? it.label.trim() : null, slug: typeof it.slug === 'string' && it.slug ? it.slug : null });
  }
  return kws.map((k) => { const m = by.get(k.keyword); return m ? { ...k, label: m.label ?? k.keyword, slug: m.slug } : k; });
}

export interface PopularRegionData {
  /** 지역 표시 이름 — 「부산」 · 「부산 해운대구」 · 「전국」 */
  label: string;
  /** 기준 문구(계약 label 그대로) */
  note: string;
  sites: PopularSite[];
  keywords: PopularKeyword[];
  fallback: boolean;
}

export const EMPTY_POPULAR: PopularRegionData = { label: '', note: POPULAR_NOTE, sites: [], keywords: [], fallback: true };

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

export function toStageTone(tone: unknown, label: unknown): StageTone {
  const t = String(tone ?? '').toLowerCase();
  if (t === 'sub' || t === 'upcoming' || t === 'redev' || t === 'default') return t;
  const l = String(label ?? '');
  if (/청약|D-\d|접수/.test(l)) return 'sub';
  if (/분양예정|분양 예정|모델하우스/.test(l)) return 'upcoming';
  if (/재개발|재건축|정비|조합/.test(l)) return 'redev';
  return 'default';
}

export function toPopularSites(rows: unknown, limit = 10): PopularSite[] {
  if (!Array.isArray(rows)) return [];
  const out: PopularSite[] = [];
  const seen = new Set<string>();
  for (const r of rows as Record<string, unknown>[]) {
    const slug = str(r?.slug);
    const name = str(r?.name);
    const rank = num(r?.rank);
    if (!slug || !name || rank === null || rank < 1 || seen.has(slug)) continue;
    seen.add(slug);
    const units = num(r.units);
    const label = str(r.stageLabel ?? r.stage_label);
    out.push({
      rank, prevRank: num(r.prevRank ?? r.prev_rank), slug, name, sigungu: str(r.sigungu),
      units: units !== null && units > 0 ? Math.round(units) : null,
      stageLabel: label, stageTone: toStageTone(r.stageTone ?? r.stage_tone, label),
    });
    const last = out[out.length - 1];
    last.thumb = popularThumbOf(r.thumb, last);
  }
  return out.sort((a, b) => a.rank - b.rank).slice(0, limit);
}

/** 1글자·자모만·숫자만·URL·8자리 이상 숫자(전화번호형)는 칩으로 내지 않는다(데이터 층도 거르지만 화면에서 한 번 더). */
export function isShowableKeyword(k: string): boolean {
  const v = k.trim();
  if ([...v].length < 2) return false;
  if (/^[ㄱ-ㅎㅏ-ㅣ\s]+$/.test(v) || /^[\d\s-]+$/.test(v)) return false;
  if (/https?:|www\.|\.(com|kr|net)\b/i.test(v)) return false;
  if (/\d{8,}/.test(v.replace(/[\s-]/g, ''))) return false;
  return true;
}

/** 계약 keywords: string[](순위 순) → 순위 칩. 객체 꼴({keyword})도 받아 준다. */
export function toPopularKeywords(rows: unknown, limit = 10): PopularKeyword[] {
  if (!Array.isArray(rows)) return [];
  const out: PopularKeyword[] = [];
  const seen = new Set<string>();
  for (const r of rows as unknown[]) {
    const keyword = typeof r === 'string' ? r.trim() : str((r as Record<string, unknown>)?.keyword);
    if (!keyword || !isShowableKeyword(keyword) || seen.has(keyword)) continue;
    seen.add(keyword);
    out.push({ rank: out.length + 1, keyword, label: keyword });
    if (out.length >= limit) break;
  }
  return out;
}

/** /api/search/popular JSON → 화면 값. fallbackLabel = 요청한 지역 이름(scope 가 비었을 때). */
export function toPopularRegionData(json: unknown, fallbackLabel: string, limit = 10): PopularRegionData {
  const j = (json ?? {}) as Record<string, unknown>;
  const label = str(j.scope) ?? fallbackLabel;
  const region = placeOf(label).region ?? (sidoKey(label) ? label : null);
  return {
    label,
    note: str(j.label) ?? POPULAR_NOTE,
    sites: toPopularSites(j.sites, limit),
    keywords: keywordChips(withServerLabels(toPopularKeywords(j.keywords, 20), j.keywordItems), toPopularSites(j.sites, 50), region).slice(0, 10),
    fallback: j.fallback === true,
  };
}

export type RankChange = { kind: 'up' | 'down' | 'same' | 'new'; text: string; aria: string };

/** 어제 대비 순위 변화 — ▲n · ▼n · – · NEW(어제 순위 없음). */
export function rankChange(rank: number, prev: number | null): RankChange {
  if (prev === null || prev < 1) return { kind: 'new', text: 'NEW', aria: '신규 진입' };
  if (prev > rank) return { kind: 'up', text: `▲${prev - rank}`, aria: `${prev - rank}계단 상승` };
  if (prev < rank) return { kind: 'down', text: `▼${rank - prev}`, aria: `${rank - prev}계단 하락` };
  return { kind: 'same', text: '–', aria: '변동 없음' };
}

/** 2열 열 우선(1~5 / 6~10) — CSS grid 행 우선 배치용으로 순서를 바꾼다. */
export function columnFirst<T>(items: readonly T[], rows = 5): T[] {
  const out: T[] = [];
  for (let r = 0; r < rows; r++) {
    if (items[r] !== undefined) out.push(items[r]);
    if (items[r + rows] !== undefined) out.push(items[r + rows]);
  }
  return out;
}

/** 「금정구 · 3,802세대」 — 값 있는 것만. */
export function popularSiteMeta(s: Pick<PopularSite, 'sigungu' | 'units'>): string {
  return subline([s.sigungu ?? '', s.units ? `${s.units.toLocaleString('ko-KR')}세대` : '']);
}

/** 계약 질의 문자열 — sido=&sigungu=[&limit][&exclude]. */
export function popularQuery(sido: string | null, sigungu: string | null, extra: { limit?: number; exclude?: string | null } = {}): string {
  const u = new URLSearchParams();
  if (sido) u.set('sido', sido);
  if (sido && sigungu) u.set('sigungu', sigungu);
  if (extra.limit) u.set('limit', String(extra.limit));
  if (extra.exclude) u.set('exclude', extra.exclude);
  return u.toString();
}

/** 결과 페이지 href — 같은 검색창으로 검색(src 는 로깅용). GlobalSearch.searchResultsHref 와 같은 꼴. */
export function searchHref(q: string, src?: string): string {
  return `/search?q=${encodeURIComponent(q.trim())}${src ? `&src=${src}` : ''}`;
}

/** 지역 칩 노드 → 계약 인자(시·도 짧은 이름 + 시·군이면 그 이름). */
export function popularArgs(node: { label: string; parent: { label: string } | null } | null): { region: string | null; sigungu: string | null } {
  if (!node) return { region: null, sigungu: null };
  return node.parent ? { region: sidoDisplayLabel(node.parent.label), sigungu: node.label } : { region: sidoDisplayLabel(node.label), sigungu: null };
}

/** 시군구 탭 응답(rpc get_popular_sigungu_tabs → sigungu, thin) → 보일 탭(thin 숨김). */
export function visibleTabs(rows: unknown): string[] {
  if (!Array.isArray(rows)) return [];
  const out: string[] = [];
  for (const r of rows as Record<string, unknown>[]) {
    const s = str(r?.sigungu);
    if (s && r?.thin !== true && !out.includes(s)) out.push(s);
  }
  return out.slice(0, 16);
}

const nsp = (s: string) => s.normalize('NFC').replace(/\s+/g, '').toLowerCase();

/**
 * #4g.3 — 인기 검색어 칩 표시: KW 원문이 붙여 쓴 꼴(「대연디아이엘」)이면 공백 무시로 일치하는 단지의 표시명(접두 제거)으로 보인다.
 * 일치 = 원문 이름 · 표시명 · 이름에서 공백·문장부호 뺀 꼴 중 하나와 공백 무시 같음. 매칭 없으면 원문.
 * 같은 단지로 모이는 칩 · 같은 글자 칩은 1개(앞 순위 유지) · 순위는 다시 1부터.
 */
export function keywordChips(keywords: readonly PopularKeyword[], sites: readonly Pick<PopularSite, 'slug' | 'name' | 'sigungu'>[], region: string | null = null): PopularKeyword[] {
  const idx = new Map<string, { slug: string; label: string }>();
  for (const st of sites) {
    const shown = displaySiteName(st.name, { region, sigungu: st.sigungu });
    for (const k of [st.name, shown, st.name.replace(/[^0-9a-zA-Z가-힣]/g, '')]) {
      const key = nsp(k);
      if (key && !idx.has(key)) idx.set(key, { slug: st.slug, label: shown });
    }
  }
  const out: PopularKeyword[] = [];
  const seenSite = new Set<string>();
  const seenLabel = new Set<string>();
  for (const k of keywords) {
    // 서버가 단지를 정해 줬으면 그것(라벨·slug) · 아니면 응답 안 인기 단지와 공백 무시 비교.
    const hit = k.slug ? { slug: k.slug, label: k.label || k.keyword } : idx.get(nsp(k.keyword));
    const label = hit ? hit.label : k.label || k.keyword;
    if (hit && seenSite.has(hit.slug)) continue;
    if (seenLabel.has(nsp(label))) continue;
    if (hit) seenSite.add(hit.slug);
    seenLabel.add(nsp(label));
    out.push({ rank: out.length + 1, keyword: k.keyword, label, ...(hit ? { slug: hit.slug } : {}) });
  }
  return out;
}
